import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Grid, 
  Paper, 
  Typography, 
  Snackbar,
  Alert
} from '@mui/material';
import { styleMappings } from './styleMappings';

const LinkedInTextGenerator = () => {
  const [inputText, setInputText] = useState('Type your text here...');
  const [fontStyles, setFontStyles] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    // Generate converted text for all styles on mount
    const styles = {};
    
    Object.keys(styleMappings).forEach(style => {
      styles[style] = convertText(inputText, styleMappings[style]);
    });
    
    setFontStyles(styles);
  }, [inputText]);

  const convertText = (text, mapping) => {
    if (!text) return '';
    
    return text.split('').map(char => {
      // Check if char is lowercase letter
      const lowerIndex = mapping.lowercase.indexOf(char.toLowerCase());
      
      if (lowerIndex !== -1) {
        // If the character is uppercase in the original text
        if (char === char.toUpperCase() && char.toLowerCase() !== char.toUpperCase()) {
          return String.fromCodePoint(mapping.upcodepoints[lowerIndex]);
        } else {
          return String.fromCodePoint(mapping.lowercodepoints[lowerIndex]);
        }
      }
      
      // If not a letter, keep the original character
      return char;
    }).join('');
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setInputText(newText);
    
    // Update all styles when text changes
    const styles = {};
    Object.keys(styleMappings).forEach(style => {
      styles[style] = convertText(newText, styleMappings[style]);
    });
    setFontStyles(styles);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarOpen(true);
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
        LinkedIn Font Generator
      </Typography>
      
      <TextField
        fullWidth
        multiline
        rows={4}
        variant="outlined"
        value={inputText}
        onChange={handleTextChange}
        placeholder="Type your text here..."
        sx={{ mb: 4 }}
      />
      
      <Grid container spacing={2}>
        {Object.keys(styleMappings).map((style) => (
          <Grid item xs={12} sm={6} md={4} key={style}>
            <Paper 
              elevation={1}
              onClick={() => copyToClipboard(fontStyles[style])}
              sx={{ 
                p: 2, 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  transform: 'translateY(-2px)',
                  boxShadow: 2
                }
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: '1.1rem', 
                  wordBreak: 'break-word',
                  minHeight: '60px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {fontStyles[style] || ''}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      
      <Typography variant="body2" align="center" sx={{ mt: 4, color: 'text.secondary' }}>
        You can use these fonts in your LinkedIn posts by simply copy pasting the text from here. Click any style to copy it. 
      </Typography>

      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={2000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LinkedInTextGenerator;